import React, { ComponentType } from 'react';

import { useIntl } from 'react-intl-next';

import Lozenge from '@atlaskit/lozenge';
import {
  LinkItem,
  NavigationHeader,
  Header,
  NestableNavigationContent,
  NestingItem,
  Section,
  SideNavigation,
} from '@atlaskit/side-navigation';

import { MenuItem, MenuItemGroup, UiEvent } from '../types';
import {
  isNestedMenu,
  findNestedMenuItemParent,
  MENU_ITEM_GROUP_TYPE,
} from '../utils/create-nested-menu';

import messages from './container-navigation-next.i18n';
import {
  BetaLozenge,
  MenuItemWrapper,
} from './container-navigation-next.style';
import PremiumIcon from './premium-icon';

type ContainerHeaderProps =
  | {
      isGlobalContext: true;
      isPrivate?: false;
      containerHref?: undefined;
      containerLogo?: undefined;
      containerName?: undefined;
      containerType?: undefined;
    }
  | {
      isGlobalContext: false;
      isPrivate: boolean;
      containerHref: string;
      containerLogo: string;
      containerName: string;
      containerType: string;
    };

export type historyPushType = {
  push: (pathname: string) => void;
};

export type ContainerNavigationProps = {
  history?: historyPushType; // This property is not passed in bitbucket-core.
  linkComponent?: ComponentType<any>;
  menuItems: MenuItem[];
  selectedMenuItem?: MenuItem;
  publishUiEvent: (event: UiEvent) => void;
};

type ContainerNavigationComponentProps = ContainerNavigationProps &
  ContainerHeaderProps;

export const ContainerMenuItem = ({
  item,
  publishUiEvent,
  selectedMenuItem,
  linkComponent,
  history,
}: {
  item: MenuItem | MenuItemGroup;
  history?: historyPushType;
  publishUiEvent: (event: UiEvent) => void;
  selectedMenuItem?: MenuItem;
  linkComponent?: ComponentType<any>;
}): JSX.Element => {
  const intl = useIntl();

  if (item.type === MENU_ITEM_GROUP_TYPE) {
    const Heading = ({ children }: { children: React.ReactNode }) => {
      return (
        <Section key={item.key} title={item.title}>
          {children}
        </Section>
      );
    };
    return (
      <Heading>
        {item.children.map(child => (
          <ContainerMenuItem
            key={child.id}
            history={history}
            item={child}
            selectedMenuItem={selectedMenuItem}
            linkComponent={linkComponent}
            publishUiEvent={publishUiEvent}
          />
        ))}
      </Heading>
    );
  }

  if (Array.isArray(item.children) && item.children.length) {
    return (
      <NestingItem id={item.id} title={item.label}>
        {item.children.map((child, index) => (
          <ContainerMenuItem
            key={`${item.id}-${index}`}
            item={child}
            publishUiEvent={publishUiEvent}
            selectedMenuItem={selectedMenuItem}
            linkComponent={linkComponent}
            history={history}
          />
        ))}
      </NestingItem>
    );
  }

  const renderMenuItem = () => {
    if (item.is_new) {
      return (
        <MenuItemWrapper>
          <span>{item.label}</span>
          <BetaLozenge>
            <Lozenge appearance="new">
              {intl.formatMessage(messages.new)}
            </Lozenge>
          </BetaLozenge>
        </MenuItemWrapper>
      );
    }
    if (item.is_beta) {
      return (
        <MenuItemWrapper>
          <span>{item.label}</span>
          <BetaLozenge>
            <Lozenge appearance="new">Beta</Lozenge>
          </BetaLozenge>
        </MenuItemWrapper>
      );
    }
    if (item.is_premium) {
      return (
        <MenuItemWrapper>
          <span>{item.label}</span>
          <PremiumIcon label="premium icon" />
        </MenuItemWrapper>
      );
    }
    return item.label;
  };

  return <LinkItem href={item.url}>{renderMenuItem()}</LinkItem>;
};

export const ContainerMenuSection = ({
  menuItems,
  publishUiEvent,
  selectedMenuItem,
  linkComponent,
  history,
}: {
  menuItems: MenuItem[];
  publishUiEvent: (event: UiEvent) => void;
  selectedMenuItem?: MenuItem;
  linkComponent?: ComponentType<any>;
  history?: historyPushType;
}): JSX.Element | null => {
  if (!selectedMenuItem) {
    return null;
  }

  const parentItem = findNestedMenuItemParent(menuItems, selectedMenuItem);
  if (!parentItem) {
    return null;
  }
  const items = parentItem ? parentItem.children : menuItems;

  if (parentItem.label) {
    return (
      <NestingItem
        key={`nested-section-${parentItem.id}`}
        id={parentItem.id}
        title={parentItem.label}
      >
        {items.map((item: MenuItem) => (
          <ContainerMenuItem
            key={item.id}
            history={history}
            item={item}
            selectedMenuItem={selectedMenuItem}
            linkComponent={linkComponent}
            publishUiEvent={publishUiEvent}
          />
        ))}
      </NestingItem>
    );
  } else {
    return (
      <>
        {items.map((item: MenuItem) => (
          <ContainerMenuItem
            key={item.id}
            history={history}
            item={item}
            selectedMenuItem={selectedMenuItem}
            linkComponent={linkComponent}
            publishUiEvent={publishUiEvent}
          />
        ))}
      </>
    );
  }
};

export const ContainerNavigation = ({
  containerName,
  linkComponent,
  menuItems,
  selectedMenuItem,
  publishUiEvent,
  history,
}: ContainerNavigationComponentProps): JSX.Element => {
  return (
    <SideNavigation label="bbc-side-nav" testId="side-navigation">
      <NavigationHeader>
        {/* TODO this header doesn't come in through the normal props as we need to load it properly */}
        <Header>Placeholder Header{containerName}</Header>
      </NavigationHeader>
      <NestableNavigationContent>
        {isNestedMenu(menuItems) ? (
          <ContainerMenuSection
            history={history}
            menuItems={menuItems}
            selectedMenuItem={selectedMenuItem}
            linkComponent={linkComponent}
            publishUiEvent={publishUiEvent}
          />
        ) : (
          menuItems.map(menuItem => {
            // TODO this section isn't tested well and needs to be understood better
            // It shouldn't throw an error, but it's not clear what the expected behavior is
            return (
              <LinkItem href={menuItem.url} key={menuItem.id}>
                {menuItem.label}
              </LinkItem>
            );
          })
        )}
      </NestableNavigationContent>
    </SideNavigation>
  );
};
